import type { QuestionPurpose } from '@course-authoring/shared-types';
import { i18n } from '@course-authoring/i18n';

/**
 * Compact response totals for the cards (PO, 2026-11-03): below 1,000 the
 * plain number, thousands rounded whole (12K), millions to one decimal
 * (2.1M). The two formatters are built once per locale, not per row; the
 * locale is the app's (i18n), falling back to the browser's.
 */
const compactFormatters = new Map<string | undefined, { whole: Intl.NumberFormat; tenths: Intl.NumberFormat }>();
export const compactCount = (count: number): string => {
  const locale = i18n.language || undefined;
  let formatters = compactFormatters.get(locale);
  if (!formatters) {
    formatters = {
      whole: new Intl.NumberFormat(locale, { notation: 'compact', maximumFractionDigits: 0 }),
      tenths: new Intl.NumberFormat(locale, { notation: 'compact', maximumFractionDigits: 1 }),
    };
    compactFormatters.set(locale, formatters);
  }
  return (count < 1_000_000 ? formatters.whole : formatters.tenths).format(count);
};

/**
 * Strings for the Question Bank browse surfaces: the page at /questions
 * (Sara's "Question Bank — Developer Handoff", 2026-11-03) and the Author
 * with AI in-chat picker (her "Choose a question — in-flow picker").
 */
export const QUESTION_BANK_BROWSE_COPY = {
  title: 'Question Bank',
  pageDescription: 'Add judgment calls, pulse checks, and reflection prompts that measure what your workforce actually thinks.',
  manageQuestions: 'Manage questions',
  searchLabel: 'Search the question bank',
  pickerSearchPlaceholder: 'Search questions',
  pageSearchPlaceholder: 'Search the question bank',
  typeLabel: 'Type',
  purposeLabel: 'Purpose',
  sortLabel: 'Sort by',
  showAll: 'Show all questions',
  questionCount: (total: number, page: number, pageSize: number): string => {
    if (total === 0) return '0 questions';
    if (total <= pageSize) return total === 1 ? '1 question' : `${total} questions`;
    const start = (page - 1) * pageSize + 1;
    const end = Math.min(page * pageSize, total);
    return `${start}–${end} of ${total} questions`;
  },
  /** The full figure, for the detail's Usage block. */
  responses: (count: number) => (count === 1 ? '1 response' : `${count.toLocaleString()} responses`),
  /** The compact figure, for the cards. */
  responsesCompact: (count: number) => (count === 1 ? '1 response' : `${compactCount(count)} responses`),
  usedIn: (count: number) => `Used in ${count} ${count === 1 ? 'lesson' : 'lessons'}`,
  added: (date: string) => `Added ${date}`,
  loading: 'Loading questions…',
  loadError: 'The question bank could not be loaded.',
  facetsError: 'The question bank filters could not be loaded.',
  retry: 'Retry',
  emptyTitle: 'No questions match these filters.',
  emptyBody: 'Clear one to see more.',
  conceptsTitle: 'Concepts',
  countsLoading: 'Loading question counts…',
  countsError: 'Question counts could not be loaded, so every concept is selectable.',
  noQuestionsForConcept: (name: string) => `${name} (no questions)`,
  conceptFallback: (id: number) => `Concept ${id}`,
  labelFallback: (id: number) => `Label ${id}`,
  usage: 'Usage',
  /** The detail's block of concept links, above the framework rows. */
  concepts: 'Concepts',
  untagged: 'No taxonomy tags. Only Likert questions with a healthy response carry them.',
  close: 'Close',
  useThisOne: 'Use this one',
  /** The picker's card controls (Sara, 2026-11-04): Details names the card's own click; Select picks the question. */
  details: 'Details',
  select: 'Select',
  selectQuestion: (text: string) => `Select ${text}`,
  startLesson: 'Start a new lesson with this question',
  startingLesson: 'Starting the lesson…',
  startError: 'The lesson could not be started. Try again.',

  panelLabel: 'Filter by skill or risk area',
  panelIntro: 'Filter by a skill or risk area to find the questions that measure it.',
  /** The picker's pill; the popover under it holds the same sections as the page's panel. */
  taxonomyPill: 'Skills & risk areas',
  taxonomyAll: 'All',
  expandAll: (name: string) => `Expand all ${name}`,
  collapseAll: (name: string) => `Collapse all ${name}`,
  checkedCount: (count: number) => (count === 1 ? '1 selected' : `${count} selected`),
  panelPlaceholderTitle: 'Only Sentiment questions measure a skill or risk area.',
  panelPlaceholderBody: (purpose: QuestionPurpose) =>
    `They do it by asking people to rate a statement about their own workplace, which is what the Culture, Risk, and Leadership frameworks read. ${purpose} questions aren't tagged against them.`,
  showSentiment: 'Show Sentiment questions',
} as const;

/** The four question types the browse surfaces offer, as authors read them. */
export const QUESTION_BANK_TYPE_LABELS: Readonly<Record<string, string>> = {
  'likert-scale': 'Likert Scale',
  'single-choice-answer': 'Single Choice',
  'workplace-color-spectrum': 'Workplace Color Spectrum',
  categorization: 'Categorization',
};
